<script setup lang="ts">
import LoadingComponent from '@toolify/client/src/modules/ui/components/LoadingComponent.vue'
import IconComponent from '@toolify/client/src/modules/ui/components/IconComponent.vue'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {SizeVariantType} from '@toolify/client/src/modules/ui/enum/SizeVariantType'
import {StyleVariantType} from '@toolify/client/src/modules/ui/enum/StyleVariantType'
import {ColorVariantType} from '@toolify/client/src/modules/ui/enum/ColorVariantType'
import {IconWeightType} from '@toolify/client/src/modules/ui/enum/IconWeightType'
import {IconBehavior} from '@toolify/client/src/modules/ui/enum/IconBehavior'
import {CursorType} from '../enum/CursorType'
import {StyleValue} from 'vue'

interface Props {
  style?: StyleVariantType
  color?: ColorVariantType
  iconNameType?: IconNameType
  iconImagePath?: string | null
  iconWeightType?: IconWeightType
  isLoading?: boolean
  isDisabled?: boolean
  round?: boolean
  hasArrow?: boolean
  hasFocus?: boolean
  size?: SizeVariantType
  iconBehavior?: IconBehavior
  iconPlacement?: 'left' | 'right'
  arrowPlacement?: 'down' | 'right'
  cursor?: CursorType
  customStyle?: StyleValue
}

const props = withDefaults(defineProps<Props>(), {
  style: StyleVariantType.FilledFilled,
  color: ColorVariantType.Primary,
  iconNameType: null,
  iconImagePath: null,
  isLoading: false,
  round: false,
  hasArrow: false,
  hasFocus: false,
  isDisabled: false,
  size: SizeVariantType.Standard,
  iconBehavior: IconBehavior.None,
  iconPlacement: 'left',
  arrowPlacement: 'down',
  cursor: CursorType.Pointer,
  customStyle: null,
})
</script>

<template>
  <button
    class="button"
    :class="[
      {'is-icon-button': !$slots.default && iconNameType},
      `theme-${color}`,
      `style-${style}`,
      `size-${size}`,
      {'is-loading': isLoading, 'is-disabled': isDisabled, 'is-round': round, 'has-focus': hasFocus, 'reverse': iconPlacement === 'right'},
    ]"
    v-bind="$attrs"
    :style="[props.customStyle, {cursor: cursor}]"
  >
    <LoadingComponent v-if="isLoading" color="white"/>
    <IconComponent
      :name="iconNameType"
      :weightType="props.iconWeightType"
      :size="props.size"
      :behavior="iconBehavior"
      v-if="iconNameType && !iconImagePath"
    />
    <img
      v-if="iconImagePath"
      :src="iconImagePath"
      :class="['icon-image', `size-${props.size}`]"
      alt=""
    />
    <div class="content" v-if="$slots.default">
      <slot/>
    </div>
    <IconComponent
      :name="IconNameType.ChevronDown"
      :weightType="props.iconWeightType"
      :size="SizeVariantType.Small"
      :class="['arrow', `arrow-${arrowPlacement}`]"
      v-if="hasArrow"
    />
  </button>
</template>

<style scoped lang="scss">
  @use '../../../styles/variables' as variables;

  .button {
    border-radius: map-get(variables.$componentRadius, standard);
    border: none;
    padding: 0 map-get(variables.$spacing, space-3);
    height: map-get(variables.$componentSizes, standard);
    cursor: pointer;
    position: relative;
    overflow: hidden;
    outline: none;
    display: flex;
    gap: map-get(variables.$spacing, space-1);
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    word-break: break-all;
    text-overflow: ellipsis;
    font-size: map-get(variables.$textSizes, text-base); 
    font-weight: 500;
    &.reverse {
      flex-direction: row-reverse;
    }

    .content{
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      line-height: 100%;
    }

    :deep(.icon) {
      flex-shrink: 0;
    }

    &.size-medium {
      padding: 0 map-get(variables.$spacing, space-1-5); 
      font-size: map-get(variables.$textSizes, text-sm);
      height: map-get(variables.$componentSizes, medium);
      border-radius: map-get(variables.$componentRadius, medium);

      :deep(.icon) {
        font-size: map-get(variables.$iconSizes, medium);
      }
    }

    &.size-large {
      padding: 0 map-get(variables.$spacing, space-6);
      font-size: map-get(variables.$textSizes, text-xl);
      height: map-get(variables.$componentSizes, large);
      border-radius: map-get(variables.$componentRadius, large);
      :deep(.icon) {
        font-size: map-get(variables.$iconSizes, large);
      }
    }

    &.is-round, &.is-icon-button.is-round {
      border-radius: 100rem;
    }

    &.is-loading {
      pointer-events: none;
      .content{
        visibility: hidden;
      }

      :deep(.icon) {
        visibility: hidden;
      }
    }

    &.is-disabled {
      pointer-events: none;
      background: map-get(variables.$colors, container) !important;
      color: map-get(variables.$colors, content-dark) !important;

      &.style-clear-filled,
      &.style-clear-clear {
        background: transparent !important;
      }

      .icon{
        color: map-get(variables.$colors, content-dark) !important;
      }
    }
  }

  .loading {
    position: absolute;
    width: 0.625rem; 
    height: 0.625rem;
  }

  .icon-image {
    display: flex;
    align-items: center;
    justify-content: center;

    &.size-large {
      width: map-get(variables.$iconSizes, large);
      height: map-get(variables.$iconSizes, large);
    }

    &.size-standard {
      width: map-get(variables.$iconSizes, standard);
      height: map-get(variables.$iconSizes, standard);
    }

    &.size-medium {
      width: map-get(variables.$iconSizes, medium);
      height: map-get(variables.$iconSizes, medium);
    }

    &.size-small {
      width: map-get(variables.$iconSizes, small);
      height: map-get(variables.$iconSizes, small);
    }

    &.size-xsmall {
      width: map-get(variables.$iconSizes, xsmall);
      height: map-get(variables.$iconSizes, xsmall);
    }
  }

  .arrow-down {
    transform: rotate(0deg);
  }
  .arrow-right {
    transform: rotate(-90deg);
  }

  .has-focus.arrow {
    :deep(.icon) {
      transform: rotate(180deg);
    }
  }

  .theme-primary{
    &.style-filled-filled{
      color: map-get(variables.$colors, on-primary);
      background: map-get(variables.$colors, primary);
      :deep(.icon) {
        color: map-get(variables.$colors, on-primary)
      }

      @media (hover: hover) and (pointer: fine) {
        &:hover{
          color: map-get(variables.$colors, on-primary-hover);
          background: map-get(variables.$colors, primary-hover);
          :deep(.icon) {
            color: map-get(variables.$colors, on-primary-hover)
          }
        }
      }
    }

    &.style-clear-filled{
      color: map-get(variables.$colors, primary);
      background: transparent;
      :deep(.icon) {
        color: map-get(variables.$colors, primary)
      }

      @media (hover: hover) and (pointer: fine) {
        &:hover{
          background: map-get(variables.$colors, container-high);
        }
      }
    }

    &.style-clear-clear{
      color: map-get(variables.$colors, primary);
      background: transparent;
      :deep(.icon) {
        color: map-get(variables.$colors, primary)
      }

      @media (hover: hover) and (pointer: fine) {
        &:hover{
          color: map-get(variables.$colors, primary-hover);
          :deep(.icon) {
            color: map-get(variables.$colors, primary-hover)
          }
        }
      }

    }
  }

  .theme-error{
    &.style-filled-filled{
      color: map-get(variables.$colors, on-error);
      background: map-get(variables.$colors, error);
      :deep(.icon) {
        color: map-get(variables.$colors, on-error)
      }

      @media (hover: hover) and (pointer: fine) {
        &:hover{
          background: map-get(variables.$colors, error-hover);
        }
      }
    }

    &.style-clear-filled{
      color: map-get(variables.$colors, error);
      background: transparent;
      :deep(.icon) {
        color: map-get(variables.$colors, error)
      }

      @media (hover: hover) and (pointer: fine) {
        &:hover{
          background: map-get(variables.$colors, container-high);
        }
      }
    }

    &.style-clear-clear{
      color: map-get(variables.$colors, error);
      background: transparent;
      :deep(.icon) {
        color: map-get(variables.$colors, error)
      }

      @media (hover: hover) and (pointer: fine) {
        &:hover{
          color: map-get(variables.$colors, error-hover);
          :deep(.icon) {
            color: map-get(variables.$colors, error-hover)
          }
        }
      }
    }
  }

  .theme-neutral{
    &.style-filled-filled{
      color: map-get(variables.$colors, content-normal);
      background: map-get(variables.$colors, container-high);
      :deep(.icon) {
        color: map-get(variables.$colors, content-normal)
      }

      @media (hover: hover) and (pointer: fine) {
        &:hover{
          background: map-get(variables.$colors, container-highest);
        }
      }
      
    }

    &.style-clear-filled{
      color: map-get(variables.$colors, content-dim);
      background: transparent;
      :deep(.icon) {
        color: map-get(variables.$colors, content-dim);
      }

      @media (hover: hover) and (pointer: fine) {
        &:hover{
          color: map-get(variables.$colors, content-normal);
          background: map-get(variables.$colors, container-high);
          :deep(.icon) {
            color: map-get(variables.$colors, content-normal);
          }
        }
      }
    }

    &.style-clear-clear{
      color: map-get(variables.$colors, content-dim);
      background: transparent;
      :deep(.icon) {
        color: map-get(variables.$colors, content-dim)
      }

      @media (hover: hover) and (pointer: fine) {
        &:hover{
          color: map-get(variables.$colors, content-normal);
          :deep(.icon) {
            color: map-get(variables.$colors, content-normal)
          }
        }
      }

      &.is-disabled{
        color: map-get(variables.$colors, content-dark);
        background: transparent !important; 
        :deep(.icon) {
          color: map-get(variables.$colors, content-dark)
        }
      }
    }
  }

  .theme-dark{
    &.style-filled-filled{
      color: map-get(variables.$colors, content-normal);
      background: map-get(variables.$colors, container);
      :deep(.icon) {
        color: map-get(variables.$colors, content-normal)
      }

      @media (hover: hover) and (pointer: fine) {
        &:hover{
          background: map-get(variables.$colors, container-high);
        }
      }
    }

    &.style-clear-filled{
      color: map-get(variables.$colors, content-dark);
      background: transparent;
      :deep(.icon) {
        color: map-get(variables.$colors, content-dark)
      }

      @media (hover: hover) and (pointer: fine) {
        &:hover{
          color: map-get(variables.$colors, content-normal);
          background: map-get(variables.$colors, container);
          :deep(.icon) {
            color: map-get(variables.$colors, content-normal);
          }
        }
      }
    }

    &.style-clear-clear{
      color: map-get(variables.$colors, content-dark);
      background: transparent;
      :deep(.icon) {
        color: map-get(variables.$colors, content-dark)
      }

      @media (hover: hover) and (pointer: fine) {
        &:hover{
          color: map-get(variables.$colors, content-normal);
          :deep(.icon) {
            color: map-get(variables.$colors, content-normal)
          }
        }
      }
    }
  }

  .theme-dim{
    &.style-filled-filled{
      color: map-get(variables.$colors, content-normal);
      background: map-get(variables.$colors, container);
      :deep(.icon) {
        color: map-get(variables.$colors, content-normal)
      }

      @media (hover: hover) and (pointer: fine) {
        &:hover{
          background: map-get(variables.$colors, container-high);
        }
      }
    }

    &.style-clear-filled{
      color: map-get(variables.$colors, content-dim);
      background: transparent;
      :deep(.icon) {
        color: map-get(variables.$colors, content-dim)
      }

      @media (hover: hover) and (pointer: fine) {
        &:hover{
          color: map-get(variables.$colors, content-normal);
          background: map-get(variables.$colors, container);
          :deep(.icon) {
            color: map-get(variables.$colors, content-normal);
          }
        }
      }
    }

    &.style-clear-clear{
      color: map-get(variables.$colors, content-dim);
      background: transparent;
      :deep(.icon) {
        color: map-get(variables.$colors, content-dim);
      }

      @media (hover: hover) and (pointer: fine) {
        &:hover{
          color: map-get(variables.$colors, content-normal);
          :deep(.icon) {
            color: map-get(variables.$colors, content-normal)
          }
        }
      }

      &.is-disabled{
        color: map-get(variables.$colors, content-dark);
        background: transparent !important;
        :deep(.icon) {
          color: map-get(variables.$colors, content-dark)
        }
      }
    }
  }

  .theme-darkest{
    &.style-filled-filled{
      color: map-get(variables.$colors, content-normal);
      background: map-get(variables.$colors, container-low);
      :deep(.icon) {
        color: map-get(variables.$colors, content-normal)
      }

      @media (hover: hover) and (pointer: fine) {
        &:hover{
          background: map-get(variables.$colors, container);
        }
      }
    }

    &.style-clear-clear{
      color: map-get(variables.$colors, content-darkest);
      background: transparent;
      :deep(.icon) {
        color: map-get(variables.$colors, content-darkest)
      }

      @media (hover: hover) and (pointer: fine) {
        &:hover{
          color: map-get(variables.$colors, content-dim);
          :deep(.icon) {
            color: map-get(variables.$colors, content-dim)
          }
        }
      }
    }

    &.style-clear-filled{
      color: map-get(variables.$colors, content-dark);
      background: transparent;
      :deep(.icon) {
        color: map-get(variables.$colors, content-dark)
      }

      @media (hover: hover) and (pointer: fine) {
        &:hover{
          color: map-get(variables.$colors, content-normal);
          background: map-get(variables.$colors, container);
          :deep(.icon) {
            color: map-get(variables.$colors, content-normal);
          }
        }
      }
    }
  }


  .button.is-icon-button{
    padding: 0;

    &.size-large{
      width: map-get(variables.$componentSizes, large);
      height: map-get(variables.$componentSizes, large);
      border-radius: map-get(variables.$componentRadius, large);
      :deep(.icon) {
        font-size: map-get(variables.$iconSizes, large);
      }
    }

    &.size-standard{
      width: map-get(variables.$componentSizes, standard);
      height: map-get(variables.$componentSizes, standard);
      border-radius: map-get(variables.$componentRadius, standard);
      :deep(.icon) {
        font-size: map-get(variables.$iconSizes, medium);
      }
    }

    &.size-medium{
      font-size: map-get(variables.$iconSizes, medium);
      width: map-get(variables.$componentSizes, medium);
      height: map-get(variables.$componentSizes, medium);
      border-radius: map-get(variables.$componentRadius, medium);
      :deep(.icon) {
        font-size: map-get(variables.$iconSizes, medium);
      }
    }

    &.size-small{
      width: map-get(variables.$componentSizes, small);
      height: map-get(variables.$componentSizes, small);
      border-radius: map-get(variables.$componentRadius, small);
      :deep(.icon) {
        font-size: map-get(variables.$iconSizes, small);
      }
    }

    &.is-round {
      // `.button.is-round` sets horizontal padding for pill buttons; for icon-only buttons
      // we must re-assert `padding: 0` with equal specificity so it stays a circle.
      padding: 0;
      border-radius: 50%;
    }

    &.theme-primary{
      &.style-clear-filled{
        background: transparent;
        :deep(.icon) {
          color: map-get(variables.$colors, on-primary)
        }

      @media (hover: hover) and (pointer: fine) {
          &:hover{
            background: map-get(variables.$colors, primary);
            :deep(.icon) {
              color: map-get(variables.$colors, on-primary-hover);
            }
          }
        }
      }

      &.style-clear-clear{
        background: transparent;
        :deep(.icon) {
          color: map-get(variables.$colors, on-primary)
        }

      @media (hover: hover) and (pointer: fine) {
          &:hover{
            :deep(.icon) {
              color: map-get(variables.$colors, on-primary-hover)
            }
          }
        }
      }
    }

    &.theme-dark{
      &.style-filled-filled{
        background: map-get(variables.$colors, container-low);
        :deep(.icon) {
          color: map-get(variables.$colors, content-normal)
        }

      @media (hover: hover) and (pointer: fine) {
          &:hover{
            background: map-get(variables.$colors, container);
          }
        }

      }

      &.style-clear-clear{
        background: transparent;
        :deep(.icon) {
          color: map-get(variables.$colors, content-dark)
        }

      @media (hover: hover) and (pointer: fine) {
          &:hover{
            :deep(.icon) {
              color: map-get(variables.$colors, content-dim)
            }
          }
        }
      }
      &.style-clear-filled {
        color: map-get(variables.$colors, content-dark);
        background: transparent;

        :deep(.icon) {
          color: map-get(variables.$colors, content-dark);
        }
        
      @media (hover: hover) and (pointer: fine) {
          &:hover {
            color: map-get(variables.$colors, content-normal);
            background: map-get(variables.$colors, container);
            :deep(.icon) {
              color: map-get(variables.$colors, content-normal);
            }
          }
        }

        &.is-disabled {
          pointer-events: none;
          opacity: 0.6;
          color: map-get(variables.$colors, content-dark);
          background: transparent !important;

          :deep(.icon) {
            color: map-get(variables.$colors, content-dark) !important;
          }
        }
      }
    }
  }
</style>
